import React, { useLayoutEffect, useState } from 'react';

import { useSankeyFormSyncer } from 'utils/syncing';

import { DIAGRAM_REFRESH_DEBOUNCE_MS } from '../../constants';
import { useSankeyFormContext } from '../../contexts/sankeyFormContext';
import {
  SankeyRenderContext,
  SankeyRenderState,
} from '../../contexts/sankeyRenderContext';
import { buildSankeyDiagram } from '../../diagram';

type Props = {
  children: React.ReactNode;
};

/*
 * This hook is used to keep the rendered diagram in sync with the Sankey form data.
 * It is called any time the form is updated without being smart about whether
 * the change will actually affect the diagram. This is intentional so that we
 * don't have to think about it when writing the form and to keep the diagram
 * logic truly decoupled from the form logic. We will optimize this again
 * once the Sankey diagram container is refactored to use React.
 */
const SankeyRenderProvider: React.FC<Props> = ({ children }) => {
  const { form, isFetching } = useSankeyFormContext();

  const [renderState, setRenderState] = useState<SankeyRenderState>();

  // Render the diagram whenever it has been (re)fetched. This includes the
  // initial diagram load and any subsequent diagram fetches due to browser
  // tab getting new focus. Autosaves flow in the other direction and don't
  // cause diagrams to be refetched.
  useLayoutEffect(() => {
    if (!isFetching) {
      setRenderState(buildSankeyDiagram(form));
    }
  }, [isFetching]);

  // Use the diagram syncer to trigger the diagram rerender on form changes.
  // This is debounced to avoid excessive rerenders while the user is typing.
  // The diagram rerender is triggered only if the form is valid.
  useSankeyFormSyncer(
    form,
    () => {
      form.handleSubmit(() => {
        setRenderState(buildSankeyDiagram(form));
      })();
    },
    DIAGRAM_REFRESH_DEBOUNCE_MS
  );

  if (!renderState) {
    return <>{children}</>;
  }

  return (
    <SankeyRenderContext.Provider value={renderState}>
      {children}
    </SankeyRenderContext.Provider>
  );
};

export default SankeyRenderProvider;
